/**
 * 多选与批量操作纯逻辑（任务 14.9；UI 接线在 MessageList）。
 *
 * 口径（legacy §6.1 + chat_screen _isSelectableForBulkAction 实读）：
 * - 可选 = 服务端已确认（id>0）的内容消息——与转发同门禁 canForwardMessage
 *   （system 注记/信令排除；失败/在途乐观行 sendState 非空且 id=0 双重排除；
 *   焚烧中排除归 14.13）。
 * - 上限 **100 条**（21s，人工要求从 20 提到 100），超限不加选，由调用方 toast。
 *   ⚠️ **与转发上限解耦**：转发仍是 20（`FORWARD_MAX_MESSAGES`，串行 200ms/帧，
 *   100 条要发 20 秒且服务端不接受帧风暴），选择上限只服务于**批量删除**
 *   （一次请求，不受帧速率约束）。选多了再点转发由调用方拦下来报，
 *   **不能默默只转前 20 条**。
 * - 全选已加载 = **从最底部（最新）往上**取可选行直到装不下（21s，人工指出
 *   「全选应该选中最底部开始计算」）。此前是从最顶上往下取，在刷屏群里
 *   选到的全是翻上去的旧消息，而用户要清的恰恰是刚刷下来的那批。
 * - 相册条目（14.1 合并网格）按整册进退：成员 id 整组选/取消——旧版逐消息行
 *   无合并形态，刻意偏差；批量转发/删除本就按张展开，语义一致。
 * - 批量操作仅转发+删除（旧版同口径）；群聊删除随菜单口径仅管理员/群主可见
 *   （普通成员不可删——func_chat.go 实锤；myRole 随 composer 透出，14.25a）。
 */
import { canForwardMessage, FORWARD_MAX_MESSAGES } from '../chat/forwardModel';
import type { TimelineItemVM } from '../chat/messageModel';

export const SELECTION_MAX = 500;
export const SELECTION_LIMIT_TEXT = `最多选择 ${SELECTION_MAX} 条消息`;

/** 条目参与批量操作的消息 id 集；[] = 不可选（行上控件置灰、点击 no-op） */
export function selectableIdsOf(item: TimelineItemVM): number[] {
  if (item.type !== 'message' || item.sendState !== undefined) return [];
  // 阅后即焚（14.13c）：secret 行不进多选（canForwardMessage 同口径；旧版仅
  // 焚烧动画期间排除，新版全程排除——forwardModel 文件头有记）
  // 端到端加密（14.14b）：encrypted 行同样不进多选（批量转发密文在对端是乱码）
  if (item.secret || item.encrypted) return [];
  if (item.content.kind === 'album') {
    // 成员张各自过门禁：与乐观行合并的册里 id=0 的张不进（回显后自然可选）
    return item.content.images.map((img) => img.id).filter((id) => id > 0);
  }
  return canForwardMessage(item.id, item.msgType) ? [item.id] : [];
}

/** 条目是否整组选中（相册=全部成员张在集合中才算） */
export function isItemSelected(selected: ReadonlySet<number>, ids: readonly number[]): boolean {
  return ids.length > 0 && ids.every((id) => selected.has(id));
}

/**
 * 点选进退（相册整组进退）。超限时集合不变、overflow=true（调用方 toast）；
 * ids 为空返回原集合。
 */
export function toggleSelection(
  selected: ReadonlySet<number>,
  ids: readonly number[],
  max: number = SELECTION_MAX,
): { next: ReadonlySet<number>; overflow: boolean } {
  if (ids.length === 0) return { next: selected, overflow: false };
  const next = new Set(selected);
  if (isItemSelected(selected, ids)) {
    for (const id of ids) next.delete(id);
    return { next, overflow: false };
  }
  for (const id of ids) next.add(id);
  if (next.size > max) return { next: selected, overflow: true };
  return { next, overflow: false };
}

/**
 * 全选已加载：**从最底部（最新）往上**逐条装入，装不下即停
 * （整册装不下整册不进）。`items` 是时间线升序，所以倒着走。
 */
export function selectAllLoaded(
  items: readonly TimelineItemVM[],
  max: number = SELECTION_MAX,
): ReadonlySet<number> {
  const out = new Set<number>();
  for (let i = items.length - 1; i >= 0; i--) {
    const ids = selectableIdsOf(items[i]!);
    if (ids.length === 0) continue;
    if (out.size + ids.length > max) break;
    for (const id of ids) out.add(id);
  }
  return out;
}

/** 选择数超过转发上限？（调用方据此拦下批量转发并报原因） */
export function exceedsForwardLimit(count: number): boolean {
  return count > FORWARD_MAX_MESSAGES;
}

export const FORWARD_LIMIT_TEXT = `最多转发 ${FORWARD_MAX_MESSAGES} 条消息，请减少选择`;
